import React, { FC, useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { routes } from "@app/routing";

/**
 * UPP-6018: campaign landing page reached from the feature-highlight email
 * (`/p/landing/:promo`). Fires a Datadog RUM Action recording the arrival,
 * the promo the visitor followed, and any campaign query params, then
 * hands off to the canonical `/sign_up` route so nothing downstream (main
 * layout, provider registration, styling, etc.) has to know this entry
 * point exists.
 *
 * The arrival ping is gated per-promo via a localStorage flag so a single
 * browser only contributes one arrival event per promo. That trades a slice
 * of accuracy (a visitor who clears storage or opens a private window still
 * counts twice) for cheap client-side deduplication of the common case: the
 * same person clicking the email link a second time. Different promos have
 * different keys, so overlapping campaigns don't suppress each other's
 * arrival counts on the same browser. Repeat arrivals still redirect to
 * `/sign_up` normally; only the Datadog event is suppressed.
 *
 * Query parameters (utm_*, tracking tokens, etc.) are preserved on the
 * redirect so campaign attribution follows the visitor into the signup
 * flow. `replace` keeps this URL out of history — Back from the signup
 * page returns wherever the visitor came from, not to a URL that would just
 * re-redirect them.
 */

const PROMO_LANDING_ARRIVAL_STORAGE_KEY = "upp:promoLandingArrived";

/** Namespaces the arrival flag under the promo so overlapping campaigns are
 * counted independently on the same browser. */
const storageKeyForPromo = (promo: string): string =>
  `${promo}:${PROMO_LANDING_ARRIVAL_STORAGE_KEY}`;

const hasFiredArrivalBefore = (promo: string): boolean => {
  try {
    return localStorage.getItem(storageKeyForPromo(promo)) === "true";
  } catch {
    // Private browsing / quota / disabled storage: treat "cannot read" as
    // "never fired" so we err on the side of sending the event.
    return false;
  }
};

const markArrivalFired = (promo: string): void => {
  try {
    localStorage.setItem(storageKeyForPromo(promo), "true");
  } catch {
    // If we can't persist the flag we would rather double-send on a later
    // visit than block the event entirely — silent no-op.
  }
};

const PromoLanding: FC = () => {
  const location = useLocation();
  const { promo } = useParams<{ promo: string }>();

  useEffect(() => {
    // Route requires `:promo`, so this should always resolve — the guard is
    // defensive against React Router edge cases and TypeScript's typing of
    // useParams as string | undefined.
    if (!promo || hasFiredArrivalBefore(promo)) return;

    // Raw search preserves whatever the campaign email put in the URL; the
    // parsed params are spread so Datadog dashboards can slice by e.g.
    // utm_campaign without substring-matching. The explicit `promo` field
    // is last so it always wins over a stray `promo` query param.
    const parsedParams = Object.fromEntries(
      new URLSearchParams(location.search)
    );
    datadogRum.addAction("promoLandingArrived", {
      queryString: location.search,
      ...parsedParams,
      promo,
    });
    markArrivalFired(promo);
  }, [promo, location.search]);

  return (
    <Navigate
      to={{ pathname: routes.signUp, search: location.search }}
      replace
    />
  );
};

export default PromoLanding;
